import { createContext, PropsWithChildren, useEffect } from "react"
import { Account } from "./components/Account/accountApi"
import { useAccount } from "./components/Account/accountQueries"
import { useSignOutMutation } from "./components/authentication/authenticationQueries"
import * as Sentry from '@sentry/browser';
import { AxiosError } from "axios";

type AuthenticationContextData = {
  currentUser: Account | undefined
}

export const AuthenticationContext = createContext<AuthenticationContextData>({
  currentUser: undefined
})

function initIntercom(user: Account) {
  const metadata: any = {
    app_id: window.env.INTERCOM_APP_ID,
    email: user.email,
    name: user.displayName,
    created_at: new Date(),
    company: {
      company_id: user.tenant.id,
      name: user.tenant.name,
      phone: user.tenant.phoneNumber,
      website: user.tenant.reviewWebsiteAddress,
      active: user.tenant.active,
      created_at: new Date(),
    }
  };

  if (user.isSuperUser) {
    metadata.company = {
      company_id: 0,
      name: 'SynkedUP Super Users',
      active: true,
      created_at: new Date(),
    }
  }
  user.roles.forEach(role => {
    metadata[role] = true
  });
  window.Intercom('boot', metadata);
}

function initPostHog(user: Account) {
  const distinctId = window.posthog.get_distinct_id()
  const emailId = user.email.toLowerCase()
  if (distinctId !== emailId) {
    window.posthog.identify(emailId, {
      email: user.email,
      userId: user.userId,
      name: user.displayName,
      tenantId: user.tenant.id,
      tenantName: user.tenant.name
    })
  }
}

const TOKEN_NAME = 'synkedup_token'

type Props = PropsWithChildren<{
  route: { current: { protected: boolean } }

}>

export const AuthenticationContextWrapper = ({ route, children }: Props) => {
  const currentUser = useAccount(route.current.protected)
  const signOutMutation = useSignOutMutation()

  useEffect(() => {
    if (currentUser.isSuccess && currentUser.data) {
      initIntercom(currentUser.data)
      initPostHog(currentUser.data)
    }
  }, [currentUser.data])

  useEffect(() => {
    if (route.current.protected && currentUser.isError) {
      const axiosError = currentUser.error as AxiosError
      if (axiosError?.response?.status === 401) {
        signOutMutation.mutate()
        localStorage.removeItem(TOKEN_NAME)
        window.Intercom('shutdown');
        window.location.href = "/#!/account";
        window.location.reload();
      } else {
        Sentry.addBreadcrumb({ category: 'File', message: 'AuthenticationContext.tsx' })
        Sentry.captureException(new Error('Failed to get user info; status code ' + axiosError?.response?.status) + ' and message: ' + axiosError?.message)
      }
    }
  }, [currentUser.isError])

  return <AuthenticationContext.Provider value={{ currentUser: currentUser.data }}>
    {children}
  </AuthenticationContext.Provider>
}

